import { useEffect, useRef, useState } from "react";

// Background video for the hero. The file is only requested once
// the section scrolls near the viewport; if the browser can't play it (or the
// user prefers reduced motion) the vector scene is shown instead.
const SRC = "/assets/coprocessor-scene.mp4";
const POSTER = "/assets/coprocessor-scene-poster.jpg";

export function SceneVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [mode, setMode] = useState<"video" | "fallback">("video");
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setMode("fallback");
      return;
    }
    const el = ref.current;
    if (!el) return;
    if (!el.canPlayType("video/mp4")) {
      setMode("fallback");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !near) return;
    el.src = SRC;
    el.play().catch(() => setMode("fallback"));
  }, [near]);

  if (mode === "fallback") return null;
  return (
    <video
      ref={ref}
      className="mk-video"
      poster={POSTER}
      muted
      loop
      playsInline
      autoPlay
      preload="none"
      aria-hidden="true"
      tabIndex={-1}
      onError={() => setMode("fallback")}
    />
  );
}
